import { Button } from "@rosc/ui/v32/button";
import { Dialog } from "@rosc/ui/v32/sheet";
import { ClipboardPaste, Keyboard, PanelRight } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { CHROME_WEB_STORE_URL } from "@/lib/chrome-extension";
import { trackOnboarding } from "@/lib/guided/events";
import { useGuidedChecklistStore } from "@/store/guidedChecklist.store";

// After Copy to chart (practice note, or the first copy while the first
// steps are open): how to paste into the EMR, the copy shortcut for next
// time, and the Chrome side panel that inserts the note directly.

const isMac = (): boolean =>
  typeof navigator !== "undefined" &&
  /mac|iphone|ipad/i.test(
    (navigator as Navigator & { userAgentData?: { platform?: string } })
      .userAgentData?.platform ?? navigator.platform,
  );

const Key = ({ children }: { children: string }): JSX.Element => (
  <kbd className="inline-flex h-6 items-center rounded-md bg-v3-panel px-1.5 font-v3 text-[12.5px] font-semibold text-v3-text shadow-v3-hairline-strong">
    {children}
  </kbd>
);

const Row = ({
  icon,
  children,
}: {
  icon: JSX.Element;
  children: React.ReactNode;
}): JSX.Element => (
  <div className="flex items-start gap-3 rounded-[12px] bg-v3-surface-2 p-3 shadow-v3-hairline">
    <span
      aria-hidden
      className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-v3-panel text-v3-text-2 shadow-v3-hairline [&_svg]:size-4"
    >
      {icon}
    </span>
    <div className="min-w-0 flex-1 text-[13.5px] leading-[1.45] text-v3-text">
      {children}
    </div>
  </div>
);

export const EmrPastePanel = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const mac = isMac();
  const paste = mac ? "⌘V" : "Ctrl+V";
  const copy = mac ? "⇧⌘C" : "Ctrl+Shift+C";
  return (
    <Dialog
      actions={
        <Button
          data-testid="emr-panel-done"
          variant="primary"
          onClick={() => onOpenChange(false)}
        >
          {t("guided.emr.done")}
        </Button>
      }
      closeLabel={t("common.close")}
      description={t("guided.emr.body")}
      open={open}
      testId="emr-panel"
      title={t("guided.emr.title")}
      onOpenChange={onOpenChange}
    >
      <div className="mt-2 space-y-2">
        <Row icon={<ClipboardPaste />}>
          <span className="flex flex-wrap items-center gap-1.5">
            {t("guided.emr.paste")} <Key>{paste}</Key>
          </span>
        </Row>
        <Row icon={<Keyboard />}>
          <span className="flex flex-wrap items-center gap-1.5">
            {t("guided.emr.copyShortcut")} <Key>{copy}</Key>
          </span>
        </Row>
        <Row icon={<PanelRight />}>
          <p className="font-semibold">{t("guided.emr.extensionTitle")}</p>
          <p className="mt-0.5 text-v3-text-2">
            {t("guided.emr.extensionBody")}
          </p>
          <Button
            asChild
            className="mt-2.5"
            data-testid="emr-panel-install"
            variant="secondary"
          >
            <a
              href={CHROME_WEB_STORE_URL}
              rel="noopener noreferrer"
              target="_blank"
              onClick={() => {
                trackOnboarding("extension_install_clicked", { from: "emr" });
                useGuidedChecklistStore.getState().complete("extension");
              }}
            >
              {t("guided.emr.install")}
            </a>
          </Button>
        </Row>
      </div>
    </Dialog>
  );
};
